import { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import {
  IconBrandTelegram,
  IconPhotoPlus,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import {
  clearSelection,
  deleteSelected,
  nextStep,
  previousStep,
  selectSelectedFileNames,
  selectThumbnails,
  useAppDispatch,
  useAppSelector,
} from "../../../store";
import { useScreenSize } from "../../../hooks";
import { Button } from "../";
import "./GalleryToolbar.scss";

/**
 * Control strip pinned to the bottom of the review masonry. It rides bare while
 * it scrolls with the content and frosts into translucent glass only once it
 * pins to its sticky bottom — an IntersectionObserver on the sentinel below it
 * toggles the `stuck` class as it stops moving. It mirrors the per-thumbnail
 * tap-to-mark interaction with two contextual states so the actions on offer
 * always match what the guest is doing:
 *
 *  - Browse (nothing marked): the primary Send fills the row with the quiet
 *    add-more action beside it.
 *  - Selection (one or more marked): the primary swaps to a destructive Delete
 *    with deselect-all beside it.
 *
 * The primary action always sits first (left), the quiet/secondary action at
 * the end. On wider screens a memory count / live "selected" summary anchors the
 * far left and the buttons keep their natural width; on narrow screens the count
 * is dropped, the secondary collapses to an icon, and the primary stretches to
 * full width. The single row never wraps. The strip keeps the gallery's own
 * actions next to the thumbnails they act on.
 */
export const GalleryToolbar = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const selectedFileNames = useAppSelector(selectSelectedFileNames);
  const thumbnails = useAppSelector(selectThumbnails);
  const isMobile = useScreenSize("mobile");

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const [isStuck, setIsStuck] = useState(false);

  // Frost the strip only once it pins to its sticky bottom. The zero-height
  // sentinel rendered just below it rides with the page; pulling the observer's
  // viewport bottom in by the toolbar's sticky `bottom` offset means the
  // sentinel leaves that inset root just as the toolbar stops moving, flipping
  // `isStuck` -> `.stuck` glass (and back when scrolling down to the end). The
  // bottom offset is dynamic — it clears the fixed nav when present (see the
  // `--toolbar-stick-bottom` override in index.scss) — so it is read from the
  // toolbar's resolved `bottom` rather than hard-coded, and re-read on resize
  // (safe-area insets shift on rotation).
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const toolbar = toolbarRef.current;
    if (!sentinel || !toolbar) return;

    let observer: IntersectionObserver | null = null;

    const observe = () => {
      observer?.disconnect();
      const bottomInset = parseFloat(getComputedStyle(toolbar).bottom) || 0;
      observer = new IntersectionObserver(
        ([entry]) => setIsStuck(!entry.isIntersecting),
        { threshold: 0, rootMargin: `0px 0px -${bottomInset}px 0px` }
      );
      observer.observe(sentinel);
    };

    observe();
    window.addEventListener("resize", observe);

    // The masonry measures thumbnail heights only after first paint, so on entry
    // the page is short and the observer above is created while the sentinel is
    // still on-screen (initial callback -> not stuck). As heights register the
    // grid grows and pushes the sentinel below the fold, but an
    // IntersectionObserver doesn't reliably re-fire for a target displaced by
    // sibling reflow — leaving the strip un-frosted until the first manual
    // scroll. Re-attach the observer whenever the surrounding section resizes so
    // its fresh initial callback reflects the settled height. The callback only
    // toggles the frosted glass (no size change), so it can't loop.
    const section = toolbar.parentElement;
    const resizeObserver = new ResizeObserver(observe);
    if (section) resizeObserver.observe(section);

    return () => {
      window.removeEventListener("resize", observe);
      resizeObserver.disconnect();
      observer?.disconnect();
    };
  }, []);

  const selectedCount = selectedFileNames.length;
  const isSelecting = selectedCount > 0;

  const addMore = useCallback(() => {
    dispatch(previousStep());
  }, [dispatch]);

  const remove = useCallback(() => {
    dispatch(deleteSelected(selectedFileNames));
  }, [dispatch, selectedFileNames]);

  const deselectAll = useCallback(() => {
    dispatch(clearSelection());
  }, [dispatch]);

  const send = useCallback(() => {
    dispatch(nextStep());
  }, [dispatch]);

  return (
    <>
      <div
        className={clsx("gallery-toolbar", { stuck: isStuck })}
        role="toolbar"
        aria-label={t("upload.review.title")}
        ref={toolbarRef}
      >
        {!isMobile && (
          <p className="gallery-toolbar-status" aria-live="polite">
            {isSelecting
              ? t("upload.review.selectedCount", { count: selectedCount })
              : t("upload.review.countLabel", { count: thumbnails.length })}
          </p>
        )}

        <div className="gallery-toolbar-actions">
          {isSelecting ? (
            <>
              <Button
                variant="error"
                icon={<IconTrash />}
                onClick={remove}
              >
                {t("upload.footer.removeBtn")}
              </Button>
              <Button
                variant="secondary"
                appearance="muted"
                icon={<IconX />}
                iconOnly={isMobile}
                onClick={deselectAll}
              >
                {t("upload.footer.deselectAllBtn")}
              </Button>
            </>
          ) : (
            <>
              <Button
                icon={<IconBrandTelegram />}
                disabled={thumbnails.length === 0}
                onClick={send}
              >
                {t("upload.footer.sendBtn")}
              </Button>
              <Button
                variant="secondary"
                appearance="muted"
                icon={<IconPhotoPlus />}
                iconOnly={isMobile}
                onClick={addMore}
              >
                {t("upload.footer.addMoreBtn")}
              </Button>
            </>
          )}
        </div>
      </div>
      <div
        className="gallery-toolbar-sentinel"
        aria-hidden="true"
        ref={sentinelRef}
      />
    </>
  );
};
